/**
 * Asset-register stored `type` catalog.
 *
 * One camelCase `type` per verb, with one `detail` branch each. The types stay separate from the
 * `chemInv*` types even where a verb is identical, so filters and display meta stay separate.
 *
 * The API does not validate `type` or `detail`. This catalog is the frontend convention that keeps
 * the history readable. See `devdocs/features/asset-register/design/events-schema.md`.
 */

import { LEDGER_ROLES, policyForLedgerRole } from './write-failure-policy'

/**
 * Stamp the derived write-failure policy onto a family of type defs.
 *
 * The role is declared once per family because every type in a family agrees about what its ledger
 * is for, and thirty repeated `ledgerRole: 'record'` keys would be noise that drifts. A single type
 * may still carry its own `ledgerRole` and it wins - the day one does, that is the interesting line
 * in the file rather than one of thirty identical ones.
 */
const withWriteFailurePolicy = (defs, familyRole) =>
    Object.fromEntries(
        Object.entries(defs).map(([key, def]) => {
            const ledgerRole = def.ledgerRole || familyRole
            return [key, { ...def, ledgerRole, onWriteFailure: policyForLedgerRole(ledgerRole) }]
        }),
    )

/** Which layer-2 row the event is about. Holdings (fodder / bulk) are assets on the same envelope. */
export const SUBJECT_KINDS = { ASSET: 'asset', HOLDING: 'holding' }

/**
 * @typedef {Object} AssetEventTypeDef
 * @property {string} type Stored envelope `type`
 * @property {string} branch `detail.<branch>` key carrying the type-specific payload
 * @property {string[]} subjects Which subject kinds may be the primary `assetIds[0]`
 * @property {string[]} [subtypes] Allowed `detail.subtype` values, where one type covers two verbs
 * @property {string} effect One line on what the event means - the reason the type exists
 * @property {string[]} freeText Branch keys that are free text, and so editable in place (see `editableEventPaths`)
 * @property {string[]} [registerPaths] Current-register paths this event can change
 * @property {'record'|'index'} ledgerRole What the ledger is for on this type - stamped per family
 * @property {'fail-closed'|'fail-open'} onWriteFailure Derived from `ledgerRole`; what happens to
 *   the primary write when the ledger write fails. See `write-failure-policy.js`
 */

/** @type {Object.<string, AssetEventTypeDef>} */
/**
 * The asset register's ledger **is** the record - `holding-ledger.js` replays a balance and
 * `AR-MOD-01` forbids storing current state - so a row without its event is a number with no
 * provenance and the write is refused. `save-entity` already behaves this way; the declaration
 * names the reason rather than leaving it in a comment at the call site.
 */
export const ASSET_EVENT_TYPES = withWriteFailurePolicy(
    {
        assetAcquire: {
            type: 'assetAcquire',
            branch: 'acquire',
            subjects: ['asset', 'holding'],
            effect: 'Cost basis and provenance of the entry',
            freeText: ['acquiredFrom', 'invoiceRef'],
            registerPaths: [
                'detail.fin.acquisitionMethod',
                'detail.fin.acquiredFrom',
                'detail.fin.acquiredDate',
                'detail.fin.readyForUseDate',
                'detail.fin.cost',
                'detail.fin.invoiceRef',
                'detail.fin.condition',
                'detail.fin.gstTreatment',
                'detail.fin.gstAmount',
                'detail.fin.capitalisedAdditions',
            ],
        },
        assetMove: { type: 'assetMove', branch: 'move', subjects: ['asset'], effect: 'Location resolution input - a declared movement', freeText: ['note'], registerPaths: ['paddockId', 'detail.latitude', 'detail.longitude', 'detail.geojson'] },
        assetContain: { type: 'assetContain', branch: 'contain', subjects: ['asset'], subtypes: ['start', 'end'], effect: 'Parent for location resolution', freeText: ['note'], registerPaths: ['detail.link.parentAssetId'] },
        assetBind: { type: 'assetBind', branch: 'bind', subjects: ['asset'], subtypes: ['start', 'end'], effect: 'Physical identifier or sensor bound to the asset (AR-REG-03, AR-LOC-11)', freeText: ['note'], registerPaths: ['sensorIds', 'detail.ident'] },
        assetLocationStocktake: { type: 'assetLocationStocktake', branch: 'locationStocktake', subjects: ['asset'], effect: 'Summary of one presence sweep - what was expected, scanned, missing and unexpected (AR-LOC-19/20)', freeText: [] },
        assetLost: { type: 'assetLost', branch: 'lost', subjects: ['asset'], effect: 'lifecycleStatus lostOrMissing, with a snapshot of the last we knew (AR-REG-15, AR-LOC-18)', freeText: ['note'], registerPaths: ['detail.reg.lifecycleStatus'] },
        assetLend: { type: 'assetLend', branch: 'lend', subjects: ['asset'], subtypes: ['out', 'return'], effect: 'lifecycleStatus borrowedOut while a loan is open (AR-TAX-20)', freeText: ['holder', 'note'], registerPaths: ['detail.reg.lifecycleStatus'] },
        assetMeter: { type: 'assetMeter', branch: 'meter', subjects: ['asset'], subtypes: ['reading', 'rollover', 'replace'], effect: 'Usage series', freeText: [], registerPaths: ['detail.meters'] },
        assetUtilise: { type: 'assetUtilise', branch: 'utilise', subjects: ['asset'], effect: 'Hours or hectares attributed to a job, paddock, crop or enterprise', freeText: ['activity', 'operator', 'note'] },
        assetDue: { type: 'assetDue', branch: 'due', subjects: ['asset'], effect: 'A maintenance item crossed its threshold - the transition, so overdue duration has a start', freeText: [] },
        assetService: { type: 'assetService', branch: 'service', subjects: ['asset'], effect: 'Closes a maintenance item', freeText: ['description', 'providerName', 'invoiceRef'], registerPaths: ['detail.maint', 'detail.reg.lifecycleStatus'] },
        assetWorkOrder: {
            type: 'assetWorkOrder',
            branch: 'workOrder',
            subjects: ['asset'],
            effect: 'A maintenance work order - fault or scheduled task, status, assignee, safety flag (AR-MNT-07..11)',
            freeText: ['description', 'status', 'priority', 'faultCategory', 'assignedUserId', 'assignedUserName', 'completedAt', 'cancelledAt', 'statusBefore', 'history'],
            registerPaths: ['detail.reg.lifecycleStatus'],
        },
        assetInspect: { type: 'assetInspect', branch: 'inspect', subjects: ['asset'], effect: 'Compliance check and its outcome', freeText: ['inspectorName', 'reference', 'findings'] },
        assetIncident: { type: 'assetIncident', branch: 'incident', subjects: ['asset'], effect: 'Damage or incident record', freeText: ['description', 'personsInvolved', 'correctiveActions'] },
        assetStatus: { type: 'assetStatus', branch: 'status', subjects: ['asset'], effect: 'lifecycleStatus transition', freeText: ['reason'], registerPaths: ['detail.reg.lifecycleStatus'] },
        assetReclassify: { type: 'assetReclassify', branch: 'reclassify', subjects: ['asset'], effect: 'Class, type or subtype changed, with the prior classification retained (AR-TAX-03)', freeText: ['reason'] },
        assetRevalue: {
            type: 'assetRevalue',
            branch: 'revalue',
            subjects: ['asset'],
            effect: 'Insured and/or market value',
            freeText: ['basis', 'valuedBy', 'reference'],
            registerPaths: ['detail.prp.insurance.replacementValue.amount', 'detail.prp.insurance.marketValue.amount', 'detail.prp.insurance.valuationMethod'],
        },
        assetReceive: { type: 'assetReceive', branch: 'holding', subjects: ['holding'], effect: 'Increases quantity', freeText: ['vendor', 'docketRef', 'note'] },
        assetIssue: { type: 'assetIssue', branch: 'holding', subjects: ['holding'], effect: 'Decreases quantity', freeText: ['buyer', 'docketRef', 'note'] },
        assetAdjust: { type: 'assetAdjust', branch: 'holding', subjects: ['holding'], effect: 'Corrects quantity, with a required reason', freeText: ['reason', 'note'] },
        assetTransfer: { type: 'assetTransfer', branch: 'holding', subjects: ['holding'], effect: 'Quantity between locations or holdings', freeText: ['note'] },
        assetFeedPlan: { type: 'assetFeedPlan', branch: 'feedPlan', subjects: ['holding'], effect: 'Standing feeding plan configuration changed, without using stock', freeText: [] },
        assetDispose: { type: 'assetDispose', branch: 'dispose', subjects: ['asset', 'holding'], effect: 'Terminates the entry', freeText: ['counterparty'], registerPaths: ['detail.reg.lifecycleStatus'] },
        assetImprove: { type: 'assetImprove', branch: 'improve', subjects: ['asset'], effect: 'Capital improvement after acquisition, classified at the time', freeText: ['description', 'invoiceRef'] },
        assetUseChange: { type: 'assetUseChange', branch: 'useChange', subjects: ['asset'], effect: 'Business use from a date, as a series rather than a stored percentage', freeText: ['reason'] },
    },
    LEDGER_ROLES.RECORD,
)

/**
 * Legacy chemical stored types recognised by the shared ledger module.
 *
 * Their domain payload stays where it is. `detail.ledger` remains optional until the chemical
 * writers adopt `attachEventLedger`, so old rows remain readable without a backfill.
 *
 * The `chemStore*` family is filed against the shed in `assetIds` rather than against the product,
 * which is the same subject rule the `asset*` types follow. It was arrived at independently by the
 * chemical register - a shed page that has to scan every chemical event on the farm to render its
 * own table is the problem it solves.
 *
 * **`ledgerRole: 'index'` for the whole family, so a failed ledger write is fail-open.** The truth
 * for chemical stock is the product's `detail.inventoryItems` cache, not the event series, and the
 * register's own writer says so: a failed ledger write must not cost a farmer their stock figures.
 * That stays legal here without licensing it for holdings, which is the entire point of declaring
 * it per consumer instead of picking one rule for the module.
 *
 * Do not read this off `ledger: 'optional'`. That is a different axis - whether a row is expected to
 * carry a `detail.ledger` envelope yet - and the two will disagree the moment chemical adopts
 * `attachEventLedger` while its cache is still the truth.
 */
export const CHEMICAL_EVENT_TYPES = withWriteFailurePolicy(
    {
        chemInvAdd: { type: 'chemInvAdd', branch: 'inventory', ledger: 'optional' },
        chemInvEdit: { type: 'chemInvEdit', branch: 'inventory', ledger: 'optional' },
        chemInvUseGeneric: { type: 'chemInvUseGeneric', branch: 'inventory', ledger: 'optional' },
        chemInvDispose: { type: 'chemInvDispose', branch: 'inventory', ledger: 'optional' },
        chemInvMove: { type: 'chemInvMove', branch: 'inventory', ledger: 'optional' },
        // CR-STK-06's single-lot reconciliation is NOT a separate stored type - corrected 17 Sep
        // after reading the real branch. A restated `remainingQty` posts as `chemInvEdit` with
        // `event.reason` one of `ADJUSTMENT_REASON_IDS` (`stocktakeVariance`, `spillage`,
        // `measurementCorrection` - `chem-inv-classes.js`). No `chemInvAdjust` type exists or is
        // needed; do not re-add one without re-reading that file first. See `stocktake.js` and
        // `devdocs/shared/ledger-and-reporting/stocktake-shared-shape-17-sep.md`.
        chemSprayApply: { type: 'chemSprayApply', branch: 'application', ledger: 'optional' },
        chemStoreStockIn: { type: 'chemStoreStockIn', branch: 'storeEvent', ledger: 'optional' },
        chemStoreStockOut: { type: 'chemStoreStockOut', branch: 'storeEvent', ledger: 'optional' },
        chemStoreDefault: { type: 'chemStoreDefault', branch: 'storeEvent', ledger: 'optional' },
        chemStoreEdit: { type: 'chemStoreEdit', branch: 'storeEvent', ledger: 'optional' },
        // `chemLpaDeclaration` - the LPA report's issue record (`CR-RPT-08`, `CR-RPT-12`, `CR-RPT-15`):
        // period, filters, report id, what each section held, who declared. An event because report
        // runs live in `farm.locale.reports`, which is editable JSON and not an audit record. A record,
        // fail-closed, because nothing else holds it.
        chemLpaDeclaration: { type: 'chemLpaDeclaration', branch: 'lpaDeclaration', ledgerRole: LEDGER_ROLES.RECORD },
        // `chemAnimalFlag` - a lifetime exposure flag or clean-feed period on animals (`CR-RSK-08` to `-10`),
        // filed against the animals in `animalIds`. A record, fail-closed: nothing else holds it, and a
        // flag lost on write would let a contaminated animal read as clear.
        chemAnimalFlag: { type: 'chemAnimalFlag', branch: 'animalFlag', ledgerRole: LEDGER_ROLES.RECORD },
    },
    LEDGER_ROLES.INDEX,
)

export const LEDGER_EVENT_TYPES = { ...ASSET_EVENT_TYPES, ...CHEMICAL_EVENT_TYPES }

export const ASSET_EVENT_TYPE_LIST = Object.keys(ASSET_EVENT_TYPES)
export const EVENT_MUTABLE_REGISTER_PATHS = Object.freeze([...new Set(Object.values(ASSET_EVENT_TYPES).flatMap(def => def.registerPaths || []))])

export const isAssetEventType = type => Boolean(type && Object.prototype.hasOwnProperty.call(ASSET_EVENT_TYPES, type))

export const assetEventTypeDef = type => (isAssetEventType(type) ? ASSET_EVENT_TYPES[type] : null)

export const isLedgerEventType = type => Boolean(type && Object.prototype.hasOwnProperty.call(LEDGER_EVENT_TYPES, type))

export const ledgerEventTypeDef = type => (isLedgerEventType(type) ? LEDGER_EVENT_TYPES[type] : null)

/**
 * Stored types a report reads that this module neither declares nor writes. Livestock owns them:
 * `animalTreatment` is the chemical register's treatment session, `movement` (subtype `offFarmExit` /
 * `dispatchTransport`) is a dispatch - stored by the app as `animalsMovement`, with `movement` the shape the first reader was written against. They are named here only so a many-asset read can ask the
 * server for them - `fetchAssetEventsForAssets` drops any type it does not recognise, and until this
 * existed `chem-treatment-history` was sent no treatments at all.
 */
export const REPORT_READ_EVENT_TYPES = Object.freeze({ animalTreatment: 'animalTreatment', movement: 'movement', animalsMovement: 'animalsMovement', obligationRecord: 'obligationRecord', obligationMet: 'obligationMet', incidentRecord: 'incidentRecord', auditRecord: 'auditRecord', assessmentRun: 'assessmentRun', riskRecord: 'riskRecord', riskControl: 'riskControl' })

export const isReportReadableEventType = type => isLedgerEventType(type) || Boolean(type && Object.prototype.hasOwnProperty.call(REPORT_READ_EVENT_TYPES, type))

/**
 * What a writer of this type does when its ledger write fails: `'fail-closed'` or `'fail-open'`.
 *
 * Returns `null` for an unregistered type rather than guessing. A caller that gets `null` is writing
 * something the module does not know about, and the answer to that is to register it - not to pick a
 * policy on its behalf.
 */
export const writeFailurePolicy = type => ledgerEventTypeDef(type)?.onWriteFailure ?? null

/**
 * Types that feed the location resolver (A7). `assetMove` is a declared movement; `assetContain`
 * resolves through a parent. Both sit above a declared fixed location and below a tracker fix.
 */
export const LOCATION_EVENT_TYPES = ['assetMove', 'assetContain']

/**
 * Correcting an event. Craig, 17 Sep 2026, *D2 + reversal marker*, superseding 9 Sep's "correcting an
 * event is an edit":
 *
 * - Free text and attachments stay editable in place with `PUT /events/:id` - the event's `notes`, its
 *   `detail.ledger.attachmentIds`, and the branch keys each type declares as `freeText`.
 * - Anything a figure reads - time, quantity, unit, meter value, position, paddock, entity refs - is
 *   corrected by a reversing event (`buildAssetEventReversal`) carrying `detail.ledger.reversesEventId`
 *   and a reason. The original stays.
 *
 * `PUT` itself still replaces every field and `DELETE` still soft-deletes; the allowlist is this
 * module's rule, enforced by `buildAssetEventUpdate`, not the API's.
 */
export const ALWAYS_EDITABLE_PATHS = ['detail.notes', 'detail.ledger.attachmentIds']

/** Every path an in-place edit of this type may change. Empty for a type the module does not know. */
export const editableEventPaths = type => {
    const def = assetEventTypeDef(type)
    if (!def) return []
    return [...ALWAYS_EDITABLE_PATHS, ...(def.freeText || []).map(key => `detail.${def.branch}.${key}`)]
}

export default ASSET_EVENT_TYPES
